import React, { useState } from 'react';
import './EnhancedAdminLogin.css';

interface EnhancedAdminLoginProps {
  onLoginSuccess: (adminData: any, sessionToken: string) => void;
  onClose: () => void;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '/api';

const EnhancedAdminLogin: React.FC<EnhancedAdminLoginProps> = ({
  onLoginSuccess,
  onClose
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/auth/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        localStorage.setItem('adminSessionToken', result.sessionToken);
        localStorage.setItem('adminProfile', JSON.stringify(result.admin));
        
        setTimeout(() => {
          onLoginSuccess(result.admin, result.sessionToken);
        }, 1500);
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enhanced-admin-login-overlay">
      <div className="enhanced-admin-login-container">
        {success && (
          <div className="admin-success-banner">
            Admin Login Successful
            <button className="close-success" onClick={() => setSuccess(false)}>×</button>
          </div>
        )}
        
        <div className="admin-login-header">
          <div className="admin-logo-container">
            <span className="admin-icon">🛡️</span>
          </div>
          <h1>Admin Portal</h1>
          <p>Secure administrative access</p>
        </div>

        <form className="admin-login-form" onSubmit={handleAdminLogin}>
          <div className="admin-form-group">
            <label htmlFor="admin-username">Username</label>
            <div className="input-wrapper">
              <span className="input-icon">👤</span>
              <input
                id="admin-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter admin username"
                className="admin-input"
                required
              />
            </div>
          </div>

          <div className="admin-form-group">
            <label htmlFor="admin-password">Password</label>
            <div className="input-wrapper">
              <span className="input-icon">🔒</span>
              <input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="admin-input"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="forgot-password-link">
            <button type="button" className="link-button">
              Forgot password?
            </button>
          </div>

          {error && (
            <div className="admin-error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="admin-sign-in-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="admin-security-note">
            <span className="security-icon">🔐</span>
            <p>This is a secure admin area. All activities are logged and monitored.</p>
          </div>

          <button type="button" className="admin-close-button" onClick={onClose}>
            Back to App
          </button>
        </form>
      </div>
    </div>
  );
};

export default EnhancedAdminLogin;
